/**
 * Root Cause Analysis Tab (Step 2) for NCR Edit
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Save, Send, Lock, Unlock } from 'lucide-react';
import { NCREditFormData } from './types';
import { ScoreCriteriaPanel } from './ScoreCriteriaPanel';
import { ROOT_CAUSE_DIMENSIONS } from './scoreCriteria';
import { FiveWhysSection } from './FiveWhysSection';
import { LeadTimeTracking } from './LeadTimeTracking';

interface Step2TabProps {
  formData: NCREditFormData;
  canEdit: boolean;
  saving: boolean;
  // CR-5999: per-field validation errors for the RCA section, keyed by
  // API field name (why_1, selected_root_cause, immediate_corrective_action).
  // When a key is present the corresponding Textarea renders with a red
  // border and an error message beneath it.
  fieldErrors?: Record<string, string>;
  /**
   * Whether the current user is an authorised internal user (customer /
   * Helix admin) who may override the supplier-only lock and edit the RCA
   * section directly through the portal. When true a "Master Edit" toggle
   * is shown; enabling it unlocks the fields and the Save button for the
   * internal user. The supplier's normal `canEdit` flow is unaffected.
   */
  canMasterEdit?: boolean;
  /** Who does the 8D, for the card title (E23): 'Internal', 'Customer' or
   *  'Supplier Only' (ownership.ts eightDOwnerLabel). */
  ownerLabel?: string;
  /** CR-7353: this NCR's customer has the Escape root-cause chain switched on
   *  (Admin Setup → Quality). Off → the single root-cause chain. */
  escapeRootCauseEnabled?: boolean;
  /** E28: false when the customer owns the 8D (no grading, no criteria panel). */
  graded?: boolean;
  onUpdateFormData: (field: string, value: unknown) => void;
  onSave: () => void;
  onSubmitRCA: () => void;
}

export function RootCauseTab({
  formData,
  canEdit,
  saving,
  fieldErrors = {},
  canMasterEdit = false,
  ownerLabel = 'Supplier Only',
  escapeRootCauseEnabled = false,
  graded = true,
  onUpdateFormData,
  onSave,
  onSubmitRCA,
}: Step2TabProps) {
  // Master override (customer/admin). Off by default so an internal user
  // viewing the NCR sees the supplier's read-only RCA until they explicitly
  // take over editing — avoids accidental edits to supplier-owned content.
  const [masterEditing, setMasterEditing] = useState(false);
  // Effective edit gate: the supplier's normal workflow edit OR an internal
  // user who has switched Master Edit on.
  const editable = canEdit || (canMasterEdit && masterEditing);
  const errorTextareaClass =
    'border-red-500 focus-visible:ring-red-500 focus-visible:border-red-500';
  const renderError = (field: string) =>
    fieldErrors[field] ? (
      <p
        id={`${field}-error`}
        role="alert"
        className="text-sm text-red-600 mt-1"
      >
        {fieldErrors[field]}
      </p>
    ) : null;

  return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <CardTitle>Root Cause Analysis ({ownerLabel})</CardTitle>
            {/* Master override for authorised internal users. The supplier
                owns this section; an admin/customer can take over editing
                when the supplier is unable to (e.g. on their behalf). */}
            {canMasterEdit && !canEdit && (
              <Button
                type="button"
                variant={masterEditing ? 'secondary' : 'outline'}
                size="sm"
                onClick={() => setMasterEditing((v) => !v)}
              >
                {masterEditing ? (
                  <>
                    <Unlock className="h-4 w-4 mr-2" />
                    Master Edit On
                  </>
                ) : (
                  <>
                    <Lock className="h-4 w-4 mr-2" />
                    Master Edit
                  </>
                )}
              </Button>
            )}
          </div>
          {canMasterEdit && masterEditing && !canEdit && (
            <p className="text-xs text-amber-600 mt-1">
              You are editing the supplier&rsquo;s Root Cause Analysis as an
              administrator. Changes are recorded against your account.
            </p>
          )}
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* CR-7356: scoring criteria for the Root Cause dimensions
                (Occurrence + Escape), so the supplier can see how the 8D is
                graded while completing it. Part of DE's four-dimension 8D, so
                it follows the same switch: a client without it sees the form
                it always had. */}
            {escapeRootCauseEnabled && graded && (
              <ScoreCriteriaPanel dimensions={ROOT_CAUSE_DIMENSIONS} />
            )}

            {/* E6: when the containment is due, beside it (legacy parity). */}
            <LeadTimeTracking step="containment" formData={formData} />

            {/* CR-7069: 8D D1 Team Formation */}
            <div className="border-b pb-4 mb-4">
              <h4 className="text-sm font-semibold mb-3">8D D1: Team Formation</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Team Leader / Champion</Label>
                  <Input
                    value={(formData['team_leader'] as string) || ''}
                    onChange={(e) => onUpdateFormData('team_leader', e.target.value)}
                    disabled={!editable}
                    placeholder="Name of the 8D team leader"
                  />
                </div>
                <div>
                  <Label>Team Formation Date</Label>
                  <Input
                    type="date"
                    value={(formData['team_formation_date'] as string) || ''}
                    onChange={(e) => onUpdateFormData('team_formation_date', e.target.value || null)}
                    disabled={!editable}
                  />
                </div>
              </div>
              <div className="mt-3">
                <Label>Team Members</Label>
                <Textarea
                  value={(formData['team_members'] as string) || ''}
                  onChange={(e) => onUpdateFormData('team_members', e.target.value)}
                  disabled={!editable}
                  rows={2}
                  placeholder="Enter team member names (one per line)"
                />
              </div>
            </div>

            <div>
              <Label>Immediate Containment Action *</Label>
              <Textarea
                value={formData.immediate_corrective_action || ''}
                onChange={(e) => onUpdateFormData('immediate_corrective_action', e.target.value)}
                disabled={!editable}
                rows={3}
                aria-invalid={!!fieldErrors['immediate_corrective_action']}
                aria-describedby={fieldErrors['immediate_corrective_action'] ? 'immediate_corrective_action-error' : undefined}
                className={fieldErrors['immediate_corrective_action'] ? errorTextareaClass : ''}
              />
              {renderError('immediate_corrective_action')}
            </div>

            <div>
              <Label>Supplier Disposition Instructions</Label>
              <Textarea
                value={formData.disposition_instructions || ''}
                onChange={(e) => onUpdateFormData('disposition_instructions', e.target.value)}
                disabled={!editable}
                rows={2}
                maxLength={500}
              />
            </div>

            {/* CR-7353: with the Escape chain on, the RCA is split into an
                Occurrence chain and an Escape chain — rendered side by side,
                each a reusable 5-Whys + Selected Root Cause block. Off, the
                single chain renders full-width as it did before. CR-7069:
                within each chain only Why 1 + Selected are required; the
                Escape chain is optional. The shared fields above (Containment,
                Disposition) stay full-width — only the two analyses split. */}
            {escapeRootCauseEnabled ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <FiveWhysSection
                  title="Occurrence Root Cause Analysis"
                  chain="whys"
                  selectedField="selected_root_cause"
                  required
                  formData={formData}
                  fieldErrors={fieldErrors}
                  editable={editable}
                  onUpdateFormData={onUpdateFormData}
                />
                <FiveWhysSection
                  title="Escape Root Cause Analysis"
                  chain="escape_whys"
                  selectedField="escape_selected_root_cause"
                  formData={formData}
                  fieldErrors={fieldErrors}
                  editable={editable}
                  onUpdateFormData={onUpdateFormData}
                />
              </div>
            ) : (
              <FiveWhysSection
                chain="whys"
                selectedField="selected_root_cause"
                required
                formData={formData}
                fieldErrors={fieldErrors}
                editable={editable}
                onUpdateFormData={onUpdateFormData}
              />
            )}

            {canEdit && (
              <div className="flex gap-2">
                <Button onClick={onSave} disabled={saving} variant="outline">
                  <Save className="h-4 w-4 mr-2" />
                  Save
                </Button>
                <Button onClick={onSubmitRCA} disabled={saving}>
                  <Send className="h-4 w-4 mr-2" />
                  Submit RCA
                </Button>
              </div>
            )}

            {/* Master editor (internal override): Save only. Submit RCA is a
                supplier workflow transition (created -> ca_submitted) so it is
                intentionally not offered here — the admin amends content
                without advancing the supplier's stage. */}
            {!canEdit && canMasterEdit && masterEditing && (
              <div className="flex gap-2">
                <Button onClick={onSave} disabled={saving} variant="outline">
                  <Save className="h-4 w-4 mr-2" />
                  Save
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
  );
}
